import clsx from 'clsx';
import { Check, Info, TriangleAlert } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * Toasts — **escuros**, com um ícone em disco.
 *
 * Toast e não modal, não snackbar com ação: o sistema só precisa **confirmar
 * status**, sem ação corretiva, e é esse o critério de escolha da pesquisa
 * (Fundações §5.5.4). Canto inferior direito no desktop, posição recomendada;
 * no topo em mobile, onde o rodapé é ocupado pelas ações da linha.
 *
 * A versão anterior pintava a pílula inteira com a cor do estado — verde para
 * sucesso, vermelho para erro. Três problemas, e o protótipo resolve os três de uma
 * vez com fundo escuro e a cor só no ícone: uma barra verde atravessando a tela é
 * mais chamativa que a própria chamada que ela confirma; vermelho pleno transforma
 * "o Clinicorp não confirmou" em alarme; e três fundos diferentes obrigavam a
 * reconferir o contraste do texto em cada um. Sobre `#14313a`, o texto `#F2F7F7` dá
 * 12,4:1 — um par só, válido para os quatro estados.
 *
 * `aria-live="polite"` é obrigatório: sem ele a confirmação de "chamada repetida"
 * não existe para leitor de tela (docs/risk-register.md E-09).
 */

export type ToastKind = 'info' | 'success' | 'error';

interface ToastItem {
  readonly id: number;
  readonly kind: ToastKind;
  readonly message: string;
}

interface ToastApi {
  show: (message: string, kind?: ToastKind) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/**
 * Aparência por estado: disco tingido e ícone.
 *
 * Os tons são os do protótipo — teal claro para sucesso, âmbar para aviso, coral para
 * erro — e todos sobre o mesmo fundo escuro, onde claro é o que se lê.
 */
const KIND: Record<ToastKind, { readonly tile: string; readonly icon: ReactNode }> = {
  success: {
    tile: 'bg-brand-light/16 text-brand-light',
    icon: <Check aria-hidden="true" className="size-3.75" strokeWidth={2.4} />,
  },
  info: {
    tile: 'bg-[#9fbec3]/14 text-[#9fbec3]',
    icon: <Info aria-hidden="true" className="size-3.75" strokeWidth={2.4} />,
  },
  error: {
    tile: 'bg-[#e57373]/16 text-[#e57373]',
    icon: <TriangleAlert aria-hidden="true" className="size-3.75" strokeWidth={2.4} />,
  },
};

/** 2,5s — dentro da faixa de 2–3s que a pesquisa indica para confirmação rápida. */
const DISMISS_AFTER_MS = 2500;
/** Erro fica mais: quem precisa agir tem de conseguir ler. */
const ERROR_DISMISS_AFTER_MS = 6000;

export function ToastProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const [items, setItems] = useState<readonly ToastItem[]>([]);
  const nextId = useRef(1);

  const show = useCallback((message: string, kind: ToastKind = 'info') => {
    const id = nextId.current;
    nextId.current += 1;
    setItems((current) => [...current, { id, kind, message }]);

    const timeout = kind === 'error' ? ERROR_DISMISS_AFTER_MS : DISMISS_AFTER_MS;
    setTimeout(() => {
      setItems((current) => current.filter((item) => item.id !== id));
    }, timeout);
  }, []);

  const api = useMemo<ToastApi>(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed inset-x-4 top-17 z-60 flex flex-col items-center gap-2 sm:inset-x-auto sm:top-auto sm:right-6 sm:bottom-6 sm:items-end"
      >
        {items.map((item) => (
          <div
            key={item.id}
            className={clsx(
              'anim-enter pointer-events-auto flex items-center gap-3 rounded-[0.8rem] py-2.75 pr-5 pl-2.75',
              'border border-white/10 bg-linear-to-b from-[#14313a] to-[#0f2228] text-[#f2f7f7]',
              'text-[0.81rem] leading-snug font-medium shadow-[var(--shadow-toast)] sm:max-w-105',
            )}
          >
            {/*
             * Disco de 30px com o ícone do estado.
             *
             * É o que substitui o fundo colorido: a cor vira um ponto de âncora de
             * 30px, e não uma mancha do tamanho da frase. A cor **nunca** está sozinha
             * — o ícone tem forma própria (✓, !, i) e o texto diz o que aconteceu.
             */}
            <span
              className={clsx(
                'flex size-7.5 shrink-0 items-center justify-center rounded-[0.55rem]',
                KIND[item.kind].tile,
              )}
            >
              {KIND[item.kind].icon}
            </span>
            <span className="min-w-0">{item.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (api === null) throw new Error('useToast fora de ToastProvider');
  return api;
}
